import React from 'react';

import Select from 'react-select';

import {
  Badge,
  Button,
  Checkbox,
  Table,
  TableFiltering,
  TableSorting,
  TablePreferences,
  TableContentSelector,
  TablePagination,
} from 'AWS-UI-Components-React';

import { ProductGroupSelection } from './product_group_selection';
import { ReviewChanges } from './review_changes';
import { downloadFile, toDownloadFormat } from '../../../utilities';
import { isProductLineModified, accumulateProductLineById, isUnmappedGl } from '../../../utils/product_line';
import { PRODUCT_LINE_LATEST_VERSION, PRODUCT_LINE_ROOT, PRODUCT_LINE_STATUS } from '../../../constants/product_line';

const tablePageSize = 50;

const hiddenColumns = ['user', 'time', 'staged'];

const hiddenProductLineIds = ['-1'];

export class PLGLMappingTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      reviewChangesScreen: { visible: false },
      modifiedProductLinesById: {},
    };
  }

  getColumnDefinitions = (productGroupOptions, parentProductLineOptions) => {
    return [
      {
        id: 'id',
        header: 'Id',
        cell: (item) => {
          return (
            <span>
              {item.id} {this.getBadges(item)}
            </span>
          );
        },
        minWidth: '100px',
      },
      {
        id: 'name',
        header: 'Name',
        cell: (item) => item.name,
        minWidth: '200px',
      },
      {
        id: 'gls',
        header: 'Product Groups',
        cell: (item) => this.productGroupsDisplay(item, productGroupOptions),
        minWidth: '250px',
        allowLineWrap: true,
      },
      {
        id: 'parent',
        header: 'Parent',
        cell: (item) => this.parentSelectionComponent(item, parentProductLineOptions),
        minWidth: '200px',
      },
      {
        id: 'ancestorProductLine',
        header: 'Product Line Group',
        cell: (item) =>
          `${item.ancestorProductLineName ? item.ancestorProductLineName + ' (' + item.ancestorProductLine + ')' : ''}`,
        minWidth: '150px',
      },
      {
        id: 'excluded',
        header: 'Excluded',
        cell: (item) => (
          <Checkbox
            checked={item.excluded || false}
            controlId={item.id}
            onChange={() => this.onExcludedChange(event, item)}
            disabled={this.props.readOnly}
          />
        ),
      },
      {
        id: 'ignored',
        header: 'Ignored',
        cell: (item) => (
          <Checkbox
            checked={item.ignored || false}
            controlId={item.id}
            onChange={() => this.onIgnoredChange(event, item)}
            disabled={this.props.readOnly}
          />
        ),
      },
      {
        id: 'staged',
        header: 'Staged',
        cell: (item) => (item.isStaged ? 'Yes' : 'No'),
      },
      {
        id: 'user',
        header: 'Modified By User',
        cell: (item) => item.user,
      },
      {
        id: 'time',
        header: 'Modified Time',
        cell: (item) => (item.time ? new Date(item.time).toLocaleString() : '-'),
      },
      {
        id: 'comment',
        header: 'Comment',
        cell: (item) => item.comment,
      },
    ];
  };

  getBadges = (productLine) => {
    const gls = productLine.gls;
    const ignored = productLine.ignored;

    let badges = [];

    if (isUnmappedGl(gls,ignored)) {
      badges.push(
        <Badge key={`${PRODUCT_LINE_STATUS.UNMAPPED}_${productLine.id}`} color={'grey'}>
          {PRODUCT_LINE_STATUS.UNMAPPED}
        </Badge>
      );
    }

    if (productLine.isStaged) {
      badges.push(
        <Badge key={`${PRODUCT_LINE_STATUS.STAGED}_${productLine.id}`} color={'blue'}>
          {PRODUCT_LINE_STATUS.STAGED}
        </Badge>
      );
    }

    return badges.map((badge) => <span key={badge.key}>{badge} </span>);
  };

  productGroupsDisplay = (productLine, productGroupOptions) => {
    const glIds = isUnmappedGl(productLine.gls) ? [] : productLine.gls;
    const selectedOptions = productGroupOptions.filter((option) => glIds.includes(parseInt(option.value)));

    return (
      <div key={`PRODUCT_GROUP_DISPLAY_${productLine.id}_${glIds.join()}`}>
        <ProductGroupSelection
          productGroupOptions={productGroupOptions}
          selectedProductGroups={selectedOptions}
          onProductGroupSelection={(selectedOption) => this.onProductGroupSelection(productLine, selectedOption)}
          disabled={this.props.readOnly}
        />
      </div>
    );
  };

  productGroupOptions = (productGroupsById) => {
    let options = [];
    for (let id of Object.keys(productGroupsById)) {
      const productGroup = productGroupsById[id];
      options.push({
        label: `${productGroup ? productGroup.name : 'Unknown'} (${id})`,
        value: id.toString(),
      });
    }

    return options;
  };

  parentSelectionComponent = (productLine, parentProductLineOptions) => {
    const parentId = productLine && productLine.parent;
    const selected = parentProductLineOptions.find((option) => option.value === parentId);
    return (
      <div key={`PARENT_SELECTION_${productLine.id}_${parentId}`}>
        <Select
          options={parentProductLineOptions}
          defaultValue={selected}
          onChange={(selectedOption) => this.onParentProductLineChange(selectedOption, productLine)}
          isMulti={false}
          isDisabled={this.props.readOnly}
        />
      </div>
    );
  };

  tableContentSelectorOptions = (columnDefinitions) => {
    return [
      {
        options: columnDefinitions.map((entry) => {
          return {
            id: entry.id,
            label: entry.header,
            visible: !hiddenColumns.includes(entry.id),
          };
        }),
        label: '',
      },
    ];
  };

  parentProductLineOptions = (productLines) => {
    return productLines.map((productLine) => {
      return {
        value: productLine.id,
        label: `${productLine.name} (${productLine.id})`,
      };
    });
  };

  /**
   * Product lines are grouped by their first level ancestors - i.e. "Physical Consumer"
   */
  groupingAncestorProductLineById = (productLines) => {
    const firstLevelProductLines = productLines.filter((p) => p.parent === 'ALL');
    let parentProductLineToFirstLevelMapping = {};

    for (let productLineFirstLevel of firstLevelProductLines) {
      let childrenIds = productLineFirstLevel.childrenIds ? productLineFirstLevel.childrenIds.slice() : [];
      let childId = childrenIds.shift();
      while (childId) {
        if (isNaN(childId)) {
          const child = productLines.filter((p) => p.id === childId);
          childrenIds.unshift(...child[0].childrenIds);
          parentProductLineToFirstLevelMapping[childId] = productLineFirstLevel;
        }
        childId = childrenIds.shift();
      }
    }

    let groupingAncestorProductLineById = {};
    for (let leafProductLine of productLines.filter((p) => p.isLeaf)) {
      groupingAncestorProductLineById[leafProductLine.id] =
        parentProductLineToFirstLevelMapping[leafProductLine.parent];
    }

    return groupingAncestorProductLineById;
  };

  getModfiedProductLine = (productLine) => {
    const { id } = productLine;
    return this.state.modifiedProductLinesById[id]
      ? { ...this.state.modifiedProductLinesById[id] }
      : { ...productLine };
  };

  getOriginalProductLine = (productLine) => {
    const { productLinesById } = this.props;
    const { id } = productLine;
    return productLinesById[id];
  };

  onProductGroupSelection = (productLine, selectedProductGroups) => {
    const originalProductLine = this.getOriginalProductLine(productLine);
    const modifiedProductLine = this.getModfiedProductLine(productLine);

    const originalGls = originalProductLine.gls;
    const modfiedGLs = selectedProductGroups.map((item) => parseInt(item.value));
    if (!modfiedGLs.length && isUnmappedGl(originalGls)) {
      // If GL was not modified - we reset it back to the original.
      // Original GL can be 'null', [-1], etc.
      modifiedProductLine.gls = originalGls;
    } else {
      modifiedProductLine.gls = modfiedGLs;
    }

    this.handleProductLineModification(originalProductLine, modifiedProductLine);
  };

  onParentProductLineChange = (selectedOption, productLine) => {
    const { productLinesById } = this.props;
    const originalProductLine = this.getOriginalProductLine(productLine);
    const modifiedProductLine = this.getModfiedProductLine(productLine);

    const selectedParentId = selectedOption.value;
    const selectedParent = productLinesById[selectedParentId];

    modifiedProductLine.parentItem = selectedParent;
    modifiedProductLine.parent = selectedParent.id;

    this.handleProductLineModification(originalProductLine, modifiedProductLine);
  };

  onExcludedChange = (event, productLine) => {
    const originalProductLine = this.getOriginalProductLine(productLine);
    const modifiedProductLine = this.getModfiedProductLine(productLine);
    modifiedProductLine.excluded = Boolean(event.detail.checked);

    this.handleProductLineModification(originalProductLine, modifiedProductLine);
  };

  onIgnoredChange = (event, productLine) => {
    const originalProductLine = this.getOriginalProductLine(productLine);
    const modifiedProductLine = this.getModfiedProductLine(productLine);
    modifiedProductLine.ignored = Boolean(event.detail.checked);

    this.handleProductLineModification(originalProductLine, modifiedProductLine);
  };

  handleProductLineModification = (originalProductLine, modifiedProductLine) => {
    // Newly unmapped PLs get -1 (Unmapped) mapping
    if (!modifiedProductLine.gls || !modifiedProductLine.gls.length) {
      modifiedProductLine.gls = [-1]
    }

    // Newly mapped PLs lose -1 (Unmapped) mapping
    if (modifiedProductLine.gls && modifiedProductLine.gls.length > 1 && modifiedProductLine.gls.includes(-1)) {
      modifiedProductLine.gls = modifiedProductLine.gls.splice(modifiedProductLine.gls.indexOf(-1), 1)
    }

    const { id } = originalProductLine;
    let modifiedProductLinesById = {
      ...this.state.modifiedProductLinesById,
      [id]: modifiedProductLine,
    };

    this.setState({ modifiedProductLinesById }, () => this.getLatestVersion(originalProductLine));
  };

  getLatestVersion = (productLine) => {
    const { refreshPlHierarchy } = this.props;

    refreshPlHierarchy(productLine.id, PRODUCT_LINE_LATEST_VERSION, this.getLatestVersionSuccess);
  };

  getLatestVersionSuccess = (data) => {
    const { productLinesById } = this.props;
    const { modifiedProductLinesById } = this.state;

    const latestProductLine = data.root;
    const { id } = latestProductLine;
    const modifiedProductLine = modifiedProductLinesById[id];

    if (!isProductLineModified(latestProductLine, modifiedProductLine)) {
      this.unstageProductLineChanges([id], { ...productLinesById, [id]: latestProductLine });
    } else {
      this.props.stageProductLineChanges(modifiedProductLinesById);
    }

    this.setState({ modifiedProductLinesById: {} });
  };

  reviewChangesScreenDismiss = () => {
    this.setState({
      reviewChangesScreen: {
        visible: false,
        stagedProductLines: null,
        latestProductLinesById: null,
        productGroupsById: null,
      },
    });
  };

  hasStagedChanges = () => {
    const { productLinesById } = this.props;
    for (let id of Object.keys(productLinesById || {})) {
      if (productLinesById[id].isStaged && productLinesById[id].isLeaf) {
        return true;
      }
    }

    return false;
  };

  getStagedProductLines = () => {
    const { productLinesById } = this.props;
    let stagedProductLines = [];
    for (let id of Object.keys(productLinesById || {})) {
      const productLine = productLinesById[id];
      if (productLine.isStaged && productLine.isLeaf) {
        stagedProductLines.push(productLinesById[id]);
      }
    }

    return stagedProductLines;
  };

  onClickReviewPublishChanges = () => {
    const { refreshPlHierarchy } = this.props;

    refreshPlHierarchy(PRODUCT_LINE_ROOT, PRODUCT_LINE_LATEST_VERSION, this.refreshPlHierarchySuccess);
  };

  onClickDownloadData = (columnDefinitions, productLinesToDisplay) => {
    const data = toDownloadFormat(columnDefinitions, productLinesToDisplay);
    downloadFile(data, 'PL_GL_Mapping.xls');
  };

  refreshPlHierarchySuccess = (data) => {
    let productLinesById = {};
    accumulateProductLineById(data.root, null, productLinesById);

    const { productGroupsById } = this.props;
    this.setState({
      reviewChangesScreen: {
        visible: true,
        stagedProductLines: this.getStagedProductLines(),
        latestProductLinesById: productLinesById,
        productGroupsById,
        comment: 'Changes by user: ' + this.props.user + ' at ' + new Date().toUTCString(),
      },
    });
  };

  unstageProductLineChanges = (selectedProductLineIds, latestProductLinesById) => {
    const { unstageProductLineChanges, productGroupsById } = this.props;

    unstageProductLineChanges(selectedProductLineIds, latestProductLinesById);

    this.setState({
      reviewChangesScreen: {
        visible: this.state.reviewChangesScreen.visible,
        stagedProductLines: this.getStagedProductLines(),
        latestProductLinesById,
        productGroupsById,
      },
    });
  };

  publishProductLineChanges = (selectedProductLineIds, latestProductLinesById, stagedProductLines) => {
    const { publishProductLineChanges, productGroupsById } = this.props;

    publishProductLineChanges(selectedProductLineIds, latestProductLinesById, stagedProductLines);

    this.setState({
      reviewChangesScreen: {
        visible: this.state.reviewChangesScreen.visible,
        stagedProductLines: this.getStagedProductLines(),
        latestProductLinesById,
        productGroupsById,
      },
    });
  };

  withAdditionalSearchableFields = (productLine, productGroupsById) => {
    return {
      ...productLine,
      _searchFieldUnmapped: isUnmappedGl(productLine.gls) ? PRODUCT_LINE_STATUS.UNMAPPED : '',
      _searchFieldStaged: productLine.isStaged ? PRODUCT_LINE_STATUS.STAGED : '',
      _searchFieldAncestor: `${productLine.ancestorProductLineName} ${productLine.ancestorProductLine}`,
      _searchFieldProductGroups: productLine.gls
        ? productLine.gls
            .map((gl) => productGroupsById[gl])
            .filter((item) => item)
            .map((productGroup) => productGroup.name)
            .join()
        : '',
    };
  };

  render() {
    const { productLinesById, productGroupsById, errorScreenMessage } = this.props;
    const { reviewChangesScreen, modifiedProductLinesById } = this.state;

    let leafProductLines = productLinesById ? Object.values(productLinesById).filter((item) => item.isLeaf) : [];
    let nonLeafProductLines = productLinesById ? Object.values(productLinesById).filter((item) => !item.isLeaf) : [];

    const groupingAncestorProductLineById = this.groupingAncestorProductLineById(Object.values(productLinesById));

    const columnDefinitions = productGroupsById
      ? this.getColumnDefinitions(
          this.productGroupOptions(productGroupsById),
          this.parentProductLineOptions(nonLeafProductLines)
        )
      : [];

    const productLinesToDisplay = leafProductLines
      .filter((p) => !hiddenProductLineIds.includes(p.id))
      .map((p) => (modifiedProductLinesById[p.id] ? modifiedProductLinesById[p.id] : p))
      .map((p) => {
        return {
          ...p,
          ancestorProductLine: groupingAncestorProductLineById[p.id] ? groupingAncestorProductLineById[p.id].id : '',
          ancestorProductLineName: groupingAncestorProductLineById[p.id]
            ? groupingAncestorProductLineById[p.id].name
            : '',
        };
      })
      .map((p) => this.withAdditionalSearchableFields(p, productGroupsById));

    return (
      <div className="pl-gl-mapping-table">
        <Table
          columnDefinitions={columnDefinitions}
          items={productLinesToDisplay}
          header={
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">
                <h2>
                  PL GL Mapping <span className="awsui-util-header-counter">({productLinesToDisplay.length})</span>
                </h2>
              </div>
              <div className="awsui-util-action-stripe-group">
                { !this.props.readOnly && (
                <Button
                    text="Review/Publish Changes"
                    variant="primary"
                    onClick={this.onClickReviewPublishChanges}
                    disabled={!this.hasStagedChanges()}
                />
                )}
                <Button
                  text="Download Data"
                  onClick={() => this.onClickDownloadData(columnDefinitions, productLinesToDisplay)}
                  icon="download"
                />
              </div>
            </div>
          }
          loadingText={'Loading product lines...'}
          loading={!productLinesToDisplay || !productLinesToDisplay.length}
          stickyHeader
        >
          <TableFiltering filteringPlaceholder="Filter product lines" />
          <TableSorting
            sortingColumn="id"
            sortableColumns={[
              { id: 'id', field: 'id' },
              { id: 'name', field: 'name' },
              { id: 'parent', field: 'parent' },
              { id: 'ancestorProductLine', field: 'ancestorProductLine' },
              { id: 'excluded', field: 'excluded' },
              { id: 'ignored', field: 'ignored' },
              { id: 'staged', field: 'staged' },
              { id: 'user', field: 'user' },
              { id: 'time', field: 'time' },
            ]}
          />
          <TablePagination pageSize={tablePageSize} />
          <TablePreferences title="Preferences" confirmLabel="Confirm" cancelLabel="Cancel">
            <TableContentSelector
              title="Select visible columns"
              options={this.tableContentSelectorOptions(columnDefinitions)}
            />
          </TablePreferences>
        </Table>
        <ReviewChanges
          {...reviewChangesScreen}
          unstageProductLineChanges={this.unstageProductLineChanges}
          publishProductLineChanges={this.publishProductLineChanges}
          onDismiss={this.reviewChangesScreenDismiss}
          errorScreenMessage={errorScreenMessage}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/config_central/pl_gl_mapping/pl_gl_mapping_table.jsx